// src/components/Calendar.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../api';
import {
  IoChevronBack,
  IoChevronForward,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoEllipseOutline,
  IoBarbell,
  IoCalendarOutline,
} from 'react-icons/io5';

// ── Status config ────────────────────────────────────────────
const STATUS_CONFIG = {
  completed: {
    label: 'Completado',
    dot: 'bg-green-500',
    badge: 'bg-green-500/20 text-green-300 border-green-500/30',
    icon: IoCheckmarkCircle,
    iconColor: 'text-green-400',
  },
  missed: {
    label: 'No realizado',
    dot: 'bg-red-500',
    badge: 'bg-red-500/20 text-red-300 border-red-500/30',
    icon: IoCloseCircle,
    iconColor: 'text-red-400',
  },
  pending: {
    label: 'Pendiente',
    dot: 'bg-blue-400',
    badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    icon: IoEllipseOutline,
    iconColor: 'text-blue-400',
  },
};

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const CalendarPage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);   // raw API events
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);

  // Build a map: 'yyyy-MM-dd' → event
  const eventMap = Object.fromEntries(events.map(e => [e.start, e]));

  const fetchEvents = useCallback(async (date) => {
    setLoading(true);
    setError('');
    try {
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);
      const startStr = format(monthStart, 'yyyy-MM-dd');
      const endStr = format(monthEnd, 'yyyy-MM-dd');
      const res = await api.get(`/me/calendar-events?start=${startStr}&end=${endStr}`);
      setEvents(res.data || []);
    } catch {
      setError('No se pudieron cargar los eventos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(currentDate); }, [currentDate, fetchEvents]);

  const goToPrev = () => { setSelectedDay(null); setCurrentDate(d => subMonths(d, 1)); };
  const goToNext = () => { setSelectedDay(null); setCurrentDate(d => addMonths(d, 1)); };
  const goToToday = () => { setSelectedDay(null); setCurrentDate(new Date()); };

  // Build grid days (Mon-start, 6 weeks)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const gridDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  // Stats
  const completedCount = events.filter(e => e.status === 'completed').length;
  const missedCount = events.filter(e => e.status === 'missed').length;
  const totalScheduled = events.length;
  const adherencePct = totalScheduled > 0 ? Math.round((completedCount / totalScheduled) * 100) : 0;

  // Selected day event
  const selectedEvent = selectedDay ? eventMap[format(selectedDay, 'yyyy-MM-dd')] : null;

  return (
    <div className="fade-up max-w-2xl">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Calendario</h1>
          <p className="text-gray-400 text-sm mt-0.5">Historial de entrenamientos</p>
        </div>
        <button
          onClick={() => navigate('/my-training')}
          className="flex items-center gap-2 bg-marizan-red hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-xl text-sm transition-colors"
        >
          <IoBarbell size={16} /> Entrenar hoy
        </button>
      </div>

      {/* Month stats strip */}
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[
          { label: 'Programados', value: totalScheduled, color: 'text-white' },
          { label: 'Completados', value: completedCount, color: 'text-green-400' },
          { label: 'Perdidos', value: missedCount, color: 'text-red-400' },
          { label: 'Adherencia', value: `${adherencePct}%`, color: 'text-blue-400' },
        ].map(s => (
          <div key={s.label} className="glass-panel rounded-xl p-3 text-center">
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Calendar card */}
      <div className="glass-panel rounded-2xl overflow-hidden">

        {/* Month nav */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <button
            onClick={goToPrev}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <IoChevronBack size={18} />
          </button>

          <div className="text-center">
            <p className="text-white font-bold capitalize">
              {format(currentDate, 'MMMM', { locale: es })}
            </p>
            <p className="text-xs text-gray-500">{format(currentDate, 'yyyy')}</p>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={goToToday}
              className="text-xs px-2.5 py-1 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-colors"
            >
              Hoy
            </button>
            <button
              onClick={goToNext}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <IoChevronForward size={18} />
            </button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-white/5">
          {DAY_NAMES.map(d => (
            <div key={d} className="py-2.5 text-center text-xs font-semibold text-gray-500">
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <div className="w-7 h-7 border-2 border-marizan-red border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {gridDays.map((day, idx) => {
              const key = format(day, 'yyyy-MM-dd');
              const event = eventMap[key];
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isTodayDate = isToday(day);
              const isSelected = selectedDay && format(selectedDay, 'yyyy-MM-dd') === key;
              const status = event?.status;
              const cfg = status ? STATUS_CONFIG[status] : null;

              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  className={`
                    relative aspect-square flex flex-col items-center justify-center gap-0.5 border-b border-r border-white/5
                    transition-colors duration-150
                    ${isCurrentMonth ? 'hover:bg-white/5' : ''}
                    ${isSelected ? 'bg-white/10' : ''}
                    ${!isCurrentMonth ? 'opacity-25' : ''}
                  `}
                >
                  {/* Today highlight */}
                  {isTodayDate ? (
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-marizan-red text-white text-sm font-bold">
                      {format(day, 'd')}
                    </span>
                  ) : (
                    <span className={`text-sm ${isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}`}>
                      {format(day, 'd')}
                    </span>
                  )}

                  {/* Status dot */}
                  {cfg && (
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-center text-red-400 text-sm py-4 border-t border-white/5">{error}</p>
        )}
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="glass-panel rounded-xl p-4 mt-4">
          <p className="text-sm font-semibold text-gray-400 mb-2 capitalize">
            {format(selectedDay, "EEEE d 'de' MMMM", { locale: es })}
          </p>
          {selectedEvent ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {React.createElement(STATUS_CONFIG[selectedEvent.status]?.icon || IoCalendarOutline, {
                  size: 22,
                  className: STATUS_CONFIG[selectedEvent.status]?.iconColor || 'text-gray-400',
                })}
                <div>
                  <p className="text-white font-medium">{selectedEvent.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_CONFIG[selectedEvent.status]?.badge}`}>
                    {STATUS_CONFIG[selectedEvent.status]?.label}
                  </span>
                </div>
              </div>
              {selectedEvent.status === 'pending' && (
                <button
                  onClick={() => navigate('/my-training')}
                  className="text-sm font-semibold text-marizan-red hover:text-red-400 transition-colors"
                >
                  Registrar →
                </button>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm flex items-center gap-2">
              <IoCalendarOutline size={16} /> Sin entrenamiento programado
            </p>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-4 px-1">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </div>
        ))}
      </div>

    </div>
  );
};

export default CalendarPage;
